import { PRODUCT_CATALOG } from '../../services/customerHealthService';
import { HealthStatus } from './types';

/**
 * Presentational metadata (RYG colors, status pills) and the configured RYG thresholds/tone
 * functions for Customer Health. Pure data — no JSX.
 */

/* ---------- status → color meta ---------- */
export const healthStatusMeta = (t: string) => (({
  healthy: { c: '#51cf66', bg: 'rgba(81,207,102,.14)', tx: '#2e7d32', label: 'Healthy' },
  watch: { c: '#f5c518', bg: 'rgba(245,197,24,.20)', tx: '#8a6b00', label: 'Watch' },
  critical: { c: '#e11900', bg: 'rgba(225,25,0,.12)', tx: '#b01400', label: 'Critical' },
} as Record<string, { c: string; bg: string; tx: string; label: string }>)[t] || { c: '#999', bg: '#f0f0f0', tx: '#666', label: '—' });

export const actionStatusMeta = (s: string) => (({
  'Open': { bg: '#eef4fc', tx: '#357abd' },
  'In Progress': { bg: 'rgba(255,140,66,.16)', tx: '#b25c14' },
  'Blocked': { bg: 'rgba(225,25,0,.12)', tx: '#b01400' },
  'Done': { bg: 'rgba(81,207,102,.14)', tx: '#2e7d32' },
} as Record<string, { bg: string; tx: string }>)[s] || { bg: '#f0f0f0', tx: '#666' });

export const dealStatusMeta = (s: string) => (({
  'Qualified': { c: '#4a90e2', bg: '#eef4fc', tx: '#357abd' },
  'Proposal': { c: '#ff8c42', bg: 'rgba(255,140,66,.16)', tx: '#b25c14' },
  'Negotiation': { c: '#0b2e9a', bg: 'rgba(11,46,154,.10)', tx: '#0b2e9a' },
  'Closed Won': { c: '#51cf66', bg: 'rgba(81,207,102,.14)', tx: '#2e7d32' },
  'Closed Lost': { c: '#e11900', bg: 'rgba(225,25,0,.12)', tx: '#b01400' },
} as Record<string, { c: string; bg: string; tx: string }>)[s] || { c: '#999', bg: '#f0f0f0', tx: '#666' });

/* ---------- RYG thresholds ---------- */
/**
 * Budget runway tone, expressed as % of annual budget still open as backlog. A big backlog means
 * plenty of contracted budget left to deliver (healthy); a thin backlog means the account is close
 * to running out (critical). Derived live so a threshold change applies retroactively everywhere.
 */
export const BACKLOG_THRESHOLDS = { healthy: 40, watch: 15 };
export const budgetToneFromBacklogPct = (backlogPct: number): HealthStatus =>
  backlogPct >= BACKLOG_THRESHOLDS.healthy ? 'healthy' : backlogPct >= BACKLOG_THRESHOLDS.watch ? 'watch' : 'critical';

/** Forecast tone, expressed as % of annual budget the forecast projects to spend. */
export const FORECAST_THRESHOLDS = { watch: 100, critical: 120 };
export const forecastToneFromPct = (forecastPct: number): HealthStatus =>
  forecastPct >= FORECAST_THRESHOLDS.critical ? 'critical' : forecastPct >= FORECAST_THRESHOLDS.watch ? 'watch' : 'healthy';

/* ---------- misc presentational ---------- */
export const avatarBg: Record<string, string> = {
  'Account Executive': '#0b2e9a',
  'CS Account Manager': '#4a90e2',
  'Executive Sponsor': '#ff6b35',
};

/** All catalog product display names — used by the deal modal's product picker. */
export const ALL_PRODUCTS = PRODUCT_CATALOG.map((p) => p.name);
